<template>
  <section class="complaint-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <span>接收小程序用户提交的投诉与建议。</span>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>类型</th>
              <th>订单号</th>
              <th>处理状态</th>
              <th>后台回复</th>
              <th>提交时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in complaints" :key="item.id">
              <td><span class="cell-text">{{ getComplaintTypeText(item.type) }}</span></td>
              <td><span class="cell-text">{{ item.orderId || '--' }}</span></td>
              <td><span :class="['status-tag', toneClass(getComplaintStatusTone(item.status))]">{{ getComplaintStatusText(item.status) }}</span></td>
              <td><span class="cell-text cell-text--wide">{{ item.content || '--' }}</span></td>
              <td>{{ item.createdAt || '--' }}</td>
              <td>
                <button class="action-btn" @click="handleReply(item)">回复</button>
                <select class="status-select" :value="item.status" @change="handleStatusChange(item, $event)">
                  <option value="PENDING">待处理</option>
                  <option value="PROCESSING">处理中</option>
                  <option value="RESOLVED">已处理</option>
                </select>
              </td>
            </tr>
            <tr v-if="!complaints.length && !loading"><td colspan="6"><el-empty description="暂无投诉建议" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="page" class="table-footer">
        <span>共 {{ page.total }} 条记录，第 {{ page.page }}/{{ totalPages }} 页</span>
        <div>
          <button :disabled="page.page <= 1" @click="emit('changePage', page.page - 1)">上一页</button>
          <button class="is-active">{{ page.page }}</button>
          <button :disabled="!page.hasMore" @click="emit('changePage', page.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { toRefs } from 'vue'
import type { PageResult } from '@jiaofa/api-types'
import type { OperationStat } from '@/data/operationDesign'
import type { AdminComplaint, ComplaintStatus } from '@/api/complaint'
import { replyComplaint, updateComplaintStatus } from '@/api/complaint'

type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  stats: OperationStat[]
  complaints: AdminComplaint[]
  page?: PageResult<AdminComplaint>
  loading: boolean
  totalPages: number
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  changePage: [page: number]
  refresh: []
}>()

const { stats, complaints, page, loading, totalPages, toneClass } = toRefs(props)

const getComplaintTypeText = (type: string): string =>
  type === 'COMPLAINT' ? '投诉' : type === 'SUGGESTION' ? '建议' : type === 'FEEDBACK' ? '反馈' : type || '--'

const getComplaintStatusText = (status?: string): string =>
  status === 'PENDING' ? '待处理' : status === 'PROCESSING' ? '处理中' : status === 'RESOLVED' ? '已处理' : status || '--'

const getComplaintStatusTone = (status?: string): StatusTone =>
  status === 'RESOLVED' ? 'success' : status === 'PROCESSING' ? 'brand' : status === 'PENDING' ? 'warning' : 'muted'

const handleReply = async (item: AdminComplaint) => {
  try {
    const { value } = await ElMessageBox.prompt(
      `投诉建议内容：${item.content}\n\n请输入回复内容`,
      '回复投诉建议',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /\S+/,
        inputErrorMessage: '回复内容不能为空',
        inputValue: item.reply || '',
      }
    )
    await replyComplaint({ id: item.id, reply: value })
    ElMessage.success('回复成功')
    emit('refresh')
  } catch {
    // 用户取消操作
  }
}

const handleStatusChange = async (item: AdminComplaint, event: Event) => {
  const target = event.target as HTMLSelectElement
  const newStatus = target.value as ComplaintStatus
  try {
    await updateComplaintStatus({ id: item.id, status: newStatus })
    ElMessage.success('状态更新成功')
    emit('refresh')
  } catch (error) {
    ElMessage.error('状态更新失败')
    target.value = item.status || 'PENDING'
  }
}
</script>

<style scoped lang="scss">
.complaint-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; display: flex; align-items: center; color: var(--jf-color-text-secondary); font-size: 13px; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.cell-text { display: inline-block; max-width: 180px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.cell-text--wide { max-width: 300px; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.action-btn { padding: 4px 12px; border: 1px solid var(--jf-color-border); border-radius: 6px; background: #fff; color: var(--jf-color-text-primary); font-size: 12px; cursor: pointer; transition: all .2s; }
.action-btn:hover { border-color: var(--jf-color-primary); color: var(--jf-color-primary); }
.status-select { padding: 4px 8px; border: 1px solid var(--jf-color-border); border-radius: 6px; background: #fff; color: var(--jf-color-text-primary); font-size: 12px; cursor: pointer; margin-left: 8px; }
.status-select:hover { border-color: var(--jf-color-primary); }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
